import React, { useEffect, useRef, useState, ReactNode, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { RxLapTimer } from "react-icons/rx";
import { logout } from "../api/authApi";
import { queryClient } from "../lib/queryClient";
import { createForceLogout, LOGOUT_BROADCAST_KEY } from "../utils/forceLogout";
const baseUrl = process.env.REACT_APP_API_URL;

/**
 * Idle Timeout Configuration
 * 
 * IDLE_THRESHOLD_MS: Time of inactivity (in milliseconds) before showing the warning modal.
 * After 45 minutes of no user activity (clicks, typing, mouse movement, scrolling, etc.),
 * the countdown modal will appear.
 * 
 * COUNTDOWN_SEC: Duration of the countdown warning (in seconds).
 * User has 60 seconds to interact with the app before auto logout.
 * 
 * 🔥 CRITICAL AUTO-LOGOUT RULES:
 * 
 * We only auto-logout when:
 * 1. User is idle for 45 minutes + countdown finished with no interaction
 * 2. Real auth failure: access token expired, refresh failed, server returns 401 (handled by apiClient)
 * 
 * We NEVER auto-logout when:
 * - User is active (clicking, typing, moving mouse, scrolling, using the CRM)
 * - Time since login exceeds 45 minutes (we use inactivity, not absolute time)
 * - Single 401 error (unless it's a blocked user or refresh token fails)
 * 
 * IMPORTANT: This system is based on ACTIVITY (lastActivityAt), NOT absolute time since login.
 * - If the user is active (using the app), they will NEVER be logged out based on time alone.
 * - Only after 45 minutes of COMPLETE inactivity will the warning appear.
 * - If the user interacts during the countdown, the timer resets and they stay logged in.
 * - The timer is reset on every user activity (click, keypress, mouse move, scroll, touch).
 * 
 * The core logic uses: idleForMs = now - lastActivityAt (NOT now - loginTime)
 */
const IDLE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour of inactivity (PRODUCTION)
const COUNTDOWN_SEC = 60;                  // 60 seconds (1 minute) countdown (PRODUCTION)

/**
 * Global last activity key - shared across all tabs
 * CRITICAL: This ensures idle timeout is based on global user activity, not per-tab
 */
const GLOBAL_LAST_ACTIVITY_KEY = 'wjs_globalLastActivityAt';

interface Props { children: ReactNode; }

function getCompanyFromPath() {
  const seg = window.location.pathname.split("/")[1];
  return seg || "";
}

function parseJwtExp(token?: string | null): number | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    // exp is in seconds
    return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
  } catch { return null; }
}

const AutoLogout: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // CRITICAL: Create forceLogout at component level so it's available in all useEffects
  const forceLogoutFn = useMemo(() => createForceLogout(queryClient), []);
  
  // CRITICAL: Use separate refs for each timer to ensure proper cleanup
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timer for idle threshold
  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null); // Interval for countdown
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timeout for actual logout

  const company = getCompanyFromPath();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SEC);
  
  // CRITICAL: Use ref to store lastActivityAt to avoid stale closures in intervals
  // This ref is synced with localStorage (global across tabs)
  const lastActivityRef = useRef<number>(Date.now());

  // --- Helpers for GLOBAL activity storage (shared across all tabs) ---
  /**
   * Set global last activity timestamp
   * CRITICAL: This updates localStorage which triggers storage events in other tabs
   */
  const setGlobalLastActivity = (ms: number) => {
    lastActivityRef.current = ms;
    localStorage.setItem(GLOBAL_LAST_ACTIVITY_KEY, String(ms));
    // Also keep legacy key for backward compatibility (can be removed later)
    localStorage.setItem("lastActivity", String(ms));
  };
  
  /**
   * Get global last activity timestamp from localStorage
   * CRITICAL: This reads the GLOBAL timestamp shared across all tabs
   */
  const getGlobalLastActivity = () => {
    const v = Number(localStorage.getItem(GLOBAL_LAST_ACTIVITY_KEY) || localStorage.getItem("lastActivity"));
    return Number.isFinite(v) && v > 0 ? v : Date.now();
  };

  /**
   * Clear ALL timers - ensures no timers are running
   * CRITICAL: This must clear all three timer refs
   */
  const clearAllTimers = () => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  };

  const handleLogout = async () => {
    // Clear all timers before logging out
    clearAllTimers();
    setShowWarning(false);
    setCountdown(COUNTDOWN_SEC);
    
    // 🔥 CRITICAL: Use forceLogout with 'inactivity_timeout' reason for security
    // This will automatically log out the user when countdown reaches 0
    forceLogoutFn({
      reason: 'inactivity_timeout', // Valid auto-logout reason for security
      source: 'AutoLogout.handleLogout',
      companySlug: company,
    });
  };

  /**
   * Start the warning countdown
   * CRITICAL: Only starts if no countdown is already running
   * Uses a deadline timestamp to calculate remaining time, not a decrementing counter
   */
  const startWarningCountdown = () => {
    // CRITICAL: Do NOT start if countdown is already running
    if (warningIntervalRef.current || logoutTimeoutRef.current) {
      return;
    }

    const deadline = Date.now() + (COUNTDOWN_SEC * 1000);
    
    // Reset countdown to full value
    setCountdown(COUNTDOWN_SEC);
    setShowWarning(true);

    // Update countdown every second
    warningIntervalRef.current = setInterval(() => {
      const remainingMs = deadline - Date.now();
      const seconds = Math.max(0, Math.round(remainingMs / 1000));
      
      setCountdown(seconds);

      if (seconds <= 0) {
        // Countdown finished - clear interval and logout will be handled by logoutTimeoutRef
        if (warningIntervalRef.current) {
          clearInterval(warningIntervalRef.current);
          warningIntervalRef.current = null;
        }
      }
    }, 1000) as any;

    // Set timeout for actual logout when countdown finishes
    logoutTimeoutRef.current = setTimeout(() => {
      // CRITICAL: Before logging out, re-check global activity using ref (always fresh)
      // If user became active in ANY tab during countdown, cancel logout
      const now = Date.now();
      const globalLastActivity = lastActivityRef.current; // Use ref, not localStorage (faster, always synced)
      const idleFor = now - globalLastActivity;
      const totalIdleRequired = IDLE_THRESHOLD_MS + (COUNTDOWN_SEC * 1000);
      
      if (idleFor >= totalIdleRequired - 1000) {
        // User is truly idle across all tabs - proceed with logout
        // Clear interval if still running
        if (warningIntervalRef.current) {
          clearInterval(warningIntervalRef.current);
          warningIntervalRef.current = null;
        }
        // Clear timeout ref
        logoutTimeoutRef.current = null;
        // Perform logout
        handleLogout();
      } else {
        // User became active in some tab during countdown - cancel logout
        cancelWarningCountdown();
        resetTimers();
      }
    }, COUNTDOWN_SEC * 1000);
  };

  /**
   * Cancel the warning countdown and reset state
   * CRITICAL: Clears ALL timers and resets countdown to full value
   */
  const cancelWarningCountdown = () => {
    clearAllTimers();
    setShowWarning(false);
    setCountdown(COUNTDOWN_SEC);
  };

  /**
   * Reset timers based on current activity timestamp
   * 
   * CRITICAL: This function uses GLOBAL lastActivityAt (shared across tabs),
   * NOT loginTime or any absolute time since login.
   * 
   * The core condition is: idleForMs = now - globalLastActivityAt
   * NOT: now - loginTime
   * 
   * NOTE: We don't set a setTimeout here anymore. Instead, we rely on the
   * periodic check (every 10 seconds) to detect idle state. This ensures
   * we always use the latest globalLastActivity from the ref.
   */
  const resetTimers = () => {
    // CRITICAL: Cancel any existing warning countdown when resetting
    if (showWarning) {
      cancelWarningCountdown();
    }
    
    // Clear any existing idle timeout (we use periodic check instead)
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    
    // NOTE: We don't set a new timeout here. The periodic check (every 10 seconds)
    // will detect when user becomes idle and start the countdown.
    // This ensures we always use the latest globalLastActivity from the ref.
  };

  const handleStayConnected = () => {
    // CRITICAL: Cancel countdown and reset timers
    cancelWarningCountdown();
    resetTimers();
  };

  // 🔥 DISABLED: Axios interceptor removed - no auto logout on 401
  // Auto logout is completely disabled - only manual logout is allowed
  // The apiClient interceptor handles 401 errors without logging out (except for BLOCKED users)

  // Initialize inactivity timer - will show warning and auto-logout after inactivity
  useEffect(() => {
    // CRITICAL: Initialize from GLOBAL lastActivity (shared across tabs)
    // If another tab has recent activity, use that timestamp
    // Otherwise, use current time
    const now = Date.now();
    const globalLastActivity = getGlobalLastActivity();
    
    // Use the most recent timestamp (either from another tab or current time)
    const initialLastActivity = Math.max(globalLastActivity, now - 1000); // Allow 1 second tolerance
    lastActivityRef.current = initialLastActivity;
    
    // Ensure localStorage has the global timestamp
    if (!localStorage.getItem(GLOBAL_LAST_ACTIVITY_KEY)) {
      setGlobalLastActivity(initialLastActivity);
    } else {
      // Sync ref with existing global value
      lastActivityRef.current = globalLastActivity;
    }

    // Reset timers - will trigger warning and auto-logout after inactivity period
    // This gives the user a full IDLE_THRESHOLD_MS period from global lastActivity
    resetTimers();
    
    // Cleanup: Clear all timers on unmount
    return () => {
      clearAllTimers();
    };
  }, []); // eslint-disable-line

  /**
   * Register user activity - updates GLOBAL lastActivityAt and resets timers
   * 
   * CRITICAL: This is the ONLY function that should update lastActivityAt.
   * It is called on every user interaction (click, keypress, mouse move, scroll, touch).
   * 
   * This function:
   * - Updates GLOBAL lastActivityAt in localStorage (shared across all tabs)
   * - Updates local ref for this tab
   * - Triggers storage event in other tabs (they will cancel their countdowns)
   * 
   * This ensures that:
   * - Active users are NEVER logged out based on time alone
   * - The idle timer is based on GLOBAL inactivity across all tabs, NOT time since login
   * - Activity in ANY tab prevents idle timeout in ALL tabs
   */
  const registerActivity = useCallback(() => {
    const now = Date.now();
    
    // CRITICAL: Update GLOBAL lastActivity (shared across tabs)
    // This will trigger storage events in other tabs
    setGlobalLastActivity(now);
    
    // If warning is showing, cancel it immediately on ANY activity
    // This ensures the user can continue working without interruption
    if (showWarning) {
      cancelWarningCountdown();
    }
    
    // Always reset timers to give user another full IDLE_THRESHOLD_MS period
    resetTimers();
  }, [showWarning]);

  // CRITICAL: Listen for activity from OTHER tabs via storage events
  // When another tab calls registerActivity(), it updates localStorage,
  // which triggers this event in all other tabs
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      // Handle global activity key (for idle timeout sync)
      if (e.key === GLOBAL_LAST_ACTIVITY_KEY && e.newValue) {
        const ts = Number(e.newValue);
        if (!Number.isNaN(ts) && ts > 0) {
          // Update local ref with global activity from another tab
          lastActivityRef.current = ts;
          
          // CRITICAL: If this tab is showing the warning/countdown,
          // and another tab just had activity, cancel our countdown
          if (showWarning) {
            cancelWarningCountdown();
            resetTimers();
          } else {
            // Just reset timers to use the new global activity time
            resetTimers();
          }
        }
      }
      
      // Handle logout broadcast (for cross-tab logout sync)
      if (e.key === LOGOUT_BROADCAST_KEY && e.newValue) {
        const logoutTimestamp = Number(e.newValue);
        if (!Number.isNaN(logoutTimestamp) && logoutTimestamp > 0) {
          // Cancel any active countdown/warning
          cancelWarningCountdown();
          
          // Clear all timers
          clearAllTimers();
          
          // Trigger logout in this tab
          // Use 'cross_tab_logout' reason to indicate this is a sync logout
          try {
            forceLogoutFn('cross_tab_logout');
          } catch (err) {
            // Fallback: redirect to login manually
            const pathname = window.location.pathname;
            const isSuperadminRoute = pathname.startsWith('/superadmin');
            if (isSuperadminRoute) {
              window.location.href = '/superadmin/login';
            } else {
              // Try to extract company slug from URL
              const match = pathname.match(/^\/([^/]+)/);
              const companySlug = match ? match[1] : null;
              if (companySlug && companySlug !== 'login' && companySlug !== 'register') {
                window.location.href = `/${companySlug}/login`;
              } else {
                window.location.href = '/';
              }
            }
          }
        }
      }
    }

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [showWarning, forceLogoutFn]); // eslint-disable-line

  // CRITICAL: Periodic check for idle state using GLOBAL lastActivity
  // This runs every 10 seconds and checks if user is idle across all tabs
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const now = Date.now();
      const globalLastActivity = lastActivityRef.current;
      const idleFor = now - globalLastActivity;
      
      // Only start countdown if:
      // 1. User is idle for >= IDLE_THRESHOLD_MS
      // 2. Warning is not already showing
      // 3. No countdown is already running
      if (idleFor >= IDLE_THRESHOLD_MS && !showWarning && !warningIntervalRef.current && !logoutTimeoutRef.current) {
        startWarningCountdown();
      }
    }, 10000); // Check every 10 seconds

    return () => {
      clearInterval(checkInterval);
    };
  }, [showWarning]); // eslint-disable-line

  // --- Update last activity on user actions ---
  useEffect(() => {
    // Debounce activity tracking to avoid excessive timer resets
    let activityTimeout: ReturnType<typeof setTimeout> | null = null;
    const ACTIVITY_DEBOUNCE_MS = 1000; // Reset timer max once per second
    
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "pointerdown", "click", "keypress"];
    
    const handleActivity = () => {
      // Clear any pending debounce
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      
      // Debounce to avoid excessive resets (max once per second)
      activityTimeout = setTimeout(() => {
        registerActivity();
        activityTimeout = null;
      }, ACTIVITY_DEBOUNCE_MS);
    };
    
    // Add event listeners with passive flag for better performance
    events.forEach((ev) => {
      window.addEventListener(ev, handleActivity, { passive: true });
    });
    
    return () => {
      // Cleanup
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      events.forEach((ev) => {
        window.removeEventListener(ev, handleActivity);
      });
    };
  }, [registerActivity]); // eslint-disable-line

  // --- Mobile/browser lifecycle: persist timestamp on page freeze/background/close ---
  useEffect(() => {
    const persist = () => {
      const now = Date.now();
      setGlobalLastActivity(now);
      // When page is hidden, update global lastActivity but don't reset timers
      // This prevents the timer from expiring while the page is in background
      // CRITICAL: We update global lastActivity to "now" so when user returns, they get full IDLE_THRESHOLD_MS
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        persist();
      }
      // When returning to focus, update global activity and reset timers
      // CRITICAL: This uses global lastActivityAt (updated to "now"), NOT loginTime
      // This ensures the user has full IDLE_THRESHOLD_MS from when they return
      if (document.visibilityState === "visible") {
        const now = Date.now();
        setGlobalLastActivity(now);
        // Reset timers when page becomes visible again
        // This gives the user a fresh IDLE_THRESHOLD_MS period based on global lastActivityAt
        resetTimers();
      }
    };
    // Chrome/Android & iOS Safari-friendly hooks
    window.addEventListener("pagehide", persist);     // fires on tab/tab close & bfcache
    window.addEventListener("freeze", persist as any);// Chrome page lifecycle
    document.addEventListener("visibilitychange", onVisibility);

    // גם בבטריית טלפון/מסך כבוי, לפחות נשמור זמן אחרון כשזה קורה
    window.addEventListener("beforeunload", persist);

    return () => {
      window.removeEventListener("pagehide", persist);
      window.removeEventListener("freeze", persist as any);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", persist);
    };
  }, []); // eslint-disable-line

  // CRITICAL: Register activity on route changes
  // Navigation is a form of user activity
  useEffect(() => {
    registerActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // registerActivity is stable, no need to include it

  // --- ESC to stay connected ---
  useEffect(() => {
    if (!showWarning) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") handleStayConnected(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showWarning]);

  return (
    <>
      {children}
      {showWarning && (
        <div 
          style={{
            position: "fixed", inset: 0, zIndex: 99999, display: "flex",
            alignItems: "center", justifyContent: "center",
            background: "rgba(30,41,59,0.22)", backdropFilter: "blur(6px) saturate(110%)",
            WebkitBackdropFilter: "blur(6px) saturate(110%)", animation: "fadeInBg 0.42s",
            fontFamily: "inherit", direction: "rtl",
          }}
          onClick={handleStayConnected} // Click anywhere on background to reset
          onMouseMove={handleStayConnected} // Move mouse to reset
        >
          <div 
            style={{
              background: "rgba(255,255,255,0.88)",
              border: "1.2px solid rgba(59,130,246,0.12)",
              borderRadius: 28,
              boxShadow: "0 12px 40px 0 #1557ff1a, 0 2px 8px 0 #1e293b14",
              padding: "2.8rem 2.7rem 2.3rem 2.7rem",
              maxWidth: 430, width: "95vw", minHeight: 340,
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", textAlign: "center",
              animation: "scaleInPop 0.36s cubic-bezier(.45,1.9,.6,1.3)", position: "relative"
            }}
            onClick={(e) => e.stopPropagation()} // Prevent background click from closing modal
            onMouseMove={(e) => e.stopPropagation()} // Prevent background mouse move from closing modal
          >
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: "linear-gradient(135deg,#2563eb99 30%,#e0e7ff 100%)",
              boxShadow: "0 0 0 8px #60a5fa33",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 48, color: "#2563eb",
              margin: "-68px auto 14px auto", position: "absolute", top: -40, left: "50%",
              transform: "translateX(-50%)", animation: "popFloat 1.5s cubic-bezier(.6,1.8,.6,1.1) both"
            }}>
              <RxLapTimer style={{ filter: "drop-shadow(0 1px 7px #2563eb55)" }} />
            </div>
            <button
              style={{ position: "absolute", top: 16, left: 18, background: "none", border: "none",
                fontSize: 30, color: "#b0b8d2", cursor: "pointer", zIndex: 2, transition: "color 0.16s" }}
              onClick={handleLogout} title="התנתק עכשיו" aria-label="התנתק עכשיו"
              onMouseOver={e => (e.currentTarget.style.color = "#e11d48")}
              onMouseOut={e => (e.currentTarget.style.color = "#b0b8d2")}
            >×</button>
            <h2 style={{ fontWeight: 900, color: "#181E31", fontSize: 26, marginBottom: 7, marginTop: 38, lineHeight: 1.1, letterSpacing: ".02em", textShadow: "0 2px 18px #93c5fd33" }}>
              כמעט סיימנו! אין פעילות...
            </h2>
            <div style={{ margin: "0 0 6px 0", color: "#5a668a", fontSize: 18 }}>
              כדי להישאר מחובר/ת, לחץ/י על הכפתור או בצע/י פעולה כלשהי.<br />
              <span style={{ fontSize: 16, color: "#64748b" }}>
                (אפשר גם ללחוץ <kbd style={{ fontFamily: "monospace", background: "#e0e7ef", padding: "0.14em 0.55em", borderRadius: "6px", fontWeight: 600, fontSize: "0.96em", margin: "0 0.25em" }}>ESC</kbd>)
              </span>
            </div>
            <div style={{
              color: countdown <= 5 ? "#e11d48" : "#2563eb",
              fontWeight: 900, fontSize: 38, margin: "10px 0 26px 0",
              transition: "color 0.22s", letterSpacing: "1px",
              textShadow: countdown <= 5 ? "0 0 10px #e11d4833" : "0 0 9px #2563eb18"
            }}>
              {countdown} <span style={{ fontSize: 20, color: "#64748b", fontWeight: 500 }}>שניות</span>
            </div>
            <div style={{ display: "flex", gap: 10, width: "100%", marginBottom: 3 }}>
              <button style={{
                flex: 1, background: "linear-gradient(90deg,#2563eb 60%,#60a5fa 100%)", color: "#fff",
                fontWeight: 800, border: "none", borderRadius: 11, fontSize: 20, padding: "13px 0",
                boxShadow: "0 3px 12px 0 #2563eb33", cursor: "pointer", transition: "background 0.19s", outline: "none"
              }} onClick={handleStayConnected}>
                הישאר מחובר/ת
              </button>
              <button style={{
                flex: 1, background: "linear-gradient(90deg,#e11d48 65%,#be123c 100%)", color: "#fff",
                fontWeight: 700, border: "none", borderRadius: 11, fontSize: 18, padding: "11px 0",
                boxShadow: "0 2px 10px 0 #e11d4830", cursor: "pointer", transition: "background 0.19s"
              }} onClick={handleLogout}>
                התנתק עכשיו
              </button>
            </div>
            <div style={{ fontSize: 13, color: "#47556999", marginTop: 16 }}>
              המערכת תנתק אותך לשמירה על אבטחת מידע
            </div>
            <style>{`
              @keyframes fadeInBg { from { opacity: 0 } to { opacity: 1 } }
              @keyframes scaleInPop { from { opacity: 0; transform: scale(.85);} to { opacity: 1; transform: scale(1);} }
              @keyframes popFloat { from { opacity: 0; transform: translateY(-12px) scale(.9);} to { opacity: 1; transform: translateY(0) scale(1);} }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * AUTO-LOGOUT SUMMARY
 * 
 * This component implements an idle timeout system that ONLY logs out users when they are
 * truly inactive across ALL tabs, NOT based on absolute time since login.
 * 
 * 🔥 CRITICAL: GLOBAL IDLE ACROSS TABS
 * 
 * Idle is based on a GLOBAL lastActivity timestamp stored in localStorage (key: 'wjs_globalLastActivityAt'),
 * shared across all tabs/windows of the CRM.
 * 
 * - As long as there is activity in ANY tab, no idle timeout or auto logout will be triggered.
 * - When a tab detects activity, it updates the global timestamp in localStorage.
 * - Other tabs receive a 'storage' event and update their local refs, cancelling any countdown.
 * - Only when there is NO activity in ANY tab for 45 minutes will the countdown appear.
 * 
 * ✅ ALLOWED AUTO-LOGOUT SCENARIOS:
 * 1. Idle timeout: User inactive in ALL tabs for 45 minutes + countdown (60s) finished with no interaction
 * 2. Auth failure: Real 401 from server + refresh token failed (handled by apiClient interceptor)
 * 3. Blocked user: User is blocked by admin (handled by BlockedUserSocketModal)
 * 
 * ❌ NEVER AUTO-LOGOUT WHEN:
 * - User is active in ANY tab (clicking, typing, moving mouse, scrolling, using the CRM)
 * - Time since login exceeds 45 minutes (we use inactivity, NOT absolute time)
 * - Single 401 error (unless it's a blocked user or refresh token fails)
 * - Token expires but user is active (token refresh should handle this)
 * 
 * CORE LOGIC:
 * - Uses GLOBAL lastActivityAt (timestamp of last user interaction in ANY tab) stored in localStorage
 * - Calculates: idleForMs = Date.now() - globalLastActivityAt
 * - Does NOT use: now - loginTime (this would cause logout even for active users)
 * - Does NOT use: per-tab activity (this would cause logout in idle tabs even if user is active in another tab)
 * 
 * Every user activity (click, keypress, mouse move, scroll, touch, route change) calls registerActivity(),
 * which updates the GLOBAL lastActivityAt in localStorage to Date.now() and resets timers in all tabs.
 * 
 * Storage events ensure that when Tab A has activity, Tab B receives the update and cancels its countdown.
 * 
 * This ensures active users can work indefinitely across multiple tabs without being logged out.
 */
export default AutoLogout;
